import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button } from 'components/Form';
import Message from 'components/Message';
import { schedulePropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { IA_KEY } from 'helpers/ia-logic';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { selectActiveVenueId, selectIaLogic } from 'selectors/general';

import { SCHEDULE_LIST_V2_FILL_BAR_VARIANT, getScheduleListV2FillBar } from '../ScheduleListV2/ScheduleListV2.helpers';
import {
  getDirectBookingsCopyVariant,
  hasCampaignForClass,
  hasFillRateLoadError,
  isCreateCampaignBannerEligible,
} from './ScheduleDetailV2.helpers';
import { useScheduleFillRate, useVenueCampaigns } from './ScheduleDetailV2.hooks';
import messages from './ScheduleDetailV2.messages';
import './CreateCampaignBanner.scss';

const { block, element } = bem('create-campaign-banner');

const SEGMENT_CREATE_CAMPAIGN_CLICKED = 'create_campaign_clicked';
const SEGMENT_ORIGIN = 'insights_banner';

const CreateCampaignBanner = ({ schedule }) => {
  const navigate = useNavigate();
  const venueId = useSelector(selectActiveVenueId);
  const canAccessCampaigns = useSelector(state => selectIaLogic(state, IA_KEY.CAMPAIGNS).userHasAccess);
  const isIntegrated = getScheduleListV2FillBar(schedule).variant === SCHEDULE_LIST_V2_FILL_BAR_VARIANT.INTEGRATED;
  const isChartVisible = Boolean(schedule.smartspotEnabled) && isIntegrated;
  const { data: fillRate, isError, isSuccess } = useScheduleFillRate(schedule.scheduleId, { enabled: isChartVisible });
  const copyVariant = getDirectBookingsCopyVariant(fillRate, isError || hasFillRateLoadError(fillRate));
  const isEligible =
    isChartVisible && isSuccess && canAccessCampaigns && isCreateCampaignBannerEligible(schedule, copyVariant);
  const { data: campaigns, isSuccess: campaignsLoaded } = useVenueCampaigns({ enabled: isEligible });

  if (!isEligible || !campaignsLoaded || hasCampaignForClass(campaigns, schedule.scheduleClass?.classId)) {
    return null;
  }

  const handleCreateCampaign = () => {
    Segment.event(SEGMENT_CREATE_CAMPAIGN_CLICKED, {
      venueId,
      scheduleId: schedule.scheduleId,
      origin: SEGMENT_ORIGIN,
    });
    navigate(makePath.campaignsAdd({ venueId }));
  };

  return (
    <div className={block()} data-cypress="schedule-detail-v2-create-campaign-banner">
      <div className={element('copy')}>
        <Message as="p" className={element('title')} message={messages.createCampaignBannerTitle} />
        <Message as="p" className={element('body')} message={messages.createCampaignBannerBody} />
      </div>
      <Button className={element('cta', 'bt--sm', 'bt--text-sm')} onClick={handleCreateCampaign}>
        <Message message={messages.createCampaignBannerCta} />
      </Button>
    </div>
  );
};

CreateCampaignBanner.propTypes = {
  schedule: PropTypes.shape(schedulePropTypes).isRequired,
};

export default CreateCampaignBanner;
